import React, { useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { SearchIcon } from "lucide-react";
import { toast } from "@/components/ui/toast";
import { Card } from "@/components/ui/card";

export const GET_MOST_RECENT_BILLING_NOTES = gql`
  query GetMostRecentBillingNotes($startDate: Date, $endDate: Date) {
    mostRecentBillingNotes(startDate: $startDate, endDate: $endDate) {
      uniqueid
      odrparticipant
      cid
      cin
      initial_assessment_date
      careplan_date
      service_date
      entrydate
      lastchangedate
    }
  }
`;

export const MostRecentEvents = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const [fetchNotes, { data, loading, error }] = useLazyQuery(GET_MOST_RECENT_BILLING_NOTES);

  const handleSearch = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    fetchNotes({
      variables: {
        startDate: startDate || null,
        endDate: endDate || null
      }
    });
  };

  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const handleExport = () => {
    const notes = data?.mostRecentBillingNotes || [];
    if (!notes.length) {
      return;
    }

    const header = ['ODR Participant', 'CID', 'CIN', 'Initial Assessment Date', 'Care Plan Date', 'Service Date'];
    const rows = notes.map((note: any) => [
      note.odrparticipant,
      note.cid || '',
      note.cin || '',
      note.initial_assessment_date ? new Date(note.initial_assessment_date).toLocaleDateString() : '',
      note.careplan_date ? new Date(note.careplan_date).toLocaleDateString() : '',
      note.service_date ? new Date(note.service_date).toLocaleDateString() : ''
    ].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','));

    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'most_recent_billing_notes.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const notes = data?.mostRecentBillingNotes || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-row gap-4 items-end">
        <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
        <DatePicker date={endDate} setDate={setEndDate} label="End Date" />
        <Button onClick={handleSearch} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white">
          <SearchIcon size={16} />
          Search
        </Button>
        <Button onClick={handleReset} variant="outline">
          Reset
        </Button>
        <Button
          onClick={handleExport}
          variant="outline"
          disabled={!notes.length}
          className="ml-auto"
        >
          Export CSV
        </Button>
      </div>

      {error && <div className="text-red-600 text-sm">Error: {error.message}</div>}

      {!data && !loading && (
        <div className="p-4 text-center text-muted-foreground">
          Set date range and click Search to view notes
        </div>
      )}

      {data && notes.length === 0 && (
        <div className="p-4 text-center text-muted-foreground">
          No notes found for the selected date range
        </div>
      )}

      {notes.length > 0 && (
        <div className="space-y-0.5">
          <div className="grid grid-cols-[140px_120px_120px_160px_140px_140px] gap-3 text-xs font-semibold text-muted-foreground px-2 pb-2">
            <span>ODR Participant</span>
            <span>CID</span>
            <span>CIN</span>
            <span>Initial Assessment</span>
            <span>Care Plan</span>
            <span>Service Date</span>
          </div>
          {notes.map((note: any) => (
            <Card key={note.uniqueid} className="p-2 border-b border-t-0 border-x-0 rounded-none">
              <div className="grid grid-cols-[140px_120px_120px_160px_140px_140px] gap-3 text-xs">
                <span>{note.odrparticipant}</span>
                <span>{note.cid}</span>
                <span>{note.cin}</span>
                <span>{note.initial_assessment_date ? new Date(note.initial_assessment_date).toLocaleDateString() : ''}</span>
                <span>{note.careplan_date ? new Date(note.careplan_date).toLocaleDateString() : ''}</span>
                <span>{note.service_date ? new Date(note.service_date).toLocaleDateString() : ''}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
